import { MSG, PROTOCOL_VERSION, type ErrorReason, type OutboundMessage } from './protocol'
import { CaptchaError } from './turnstile'

export interface BridgeDeps {
  // The only legitimate counterpart is the window embedding this page.
  parentWindow: Pick<Window, 'postMessage'>
  getToken(action: string): Promise<string>
}

interface InboundEvent {
  origin: string
  source: unknown
  data: unknown
}

// Iframe side of the captcha bridge. Trust model (spec B2C-1852): there is no
// venue allowlist — the parent origin is captured from the `hello` handshake
// and every subsequent inbound message must match it. Outbound messages are
// only ever posted to that captured origin, never to '*'. Messages from any
// window other than the direct parent are dropped regardless of origin.
export function createBridge(deps: BridgeDeps) {
  let parentOrigin: string | null = null

  function post(message: OutboundMessage): void {
    if (parentOrigin === null) return
    deps.parentWindow.postMessage(message, parentOrigin)
  }

  async function handleMessage(event: InboundEvent): Promise<void> {
    if (event.source !== deps.parentWindow) return
    const data = event.data
    if (typeof data !== 'object' || data === null) return
    const { type } = data as { type?: unknown }

    if (type === MSG.HELLO) {
      // Re-hello is allowed (parent re-mount); the origin is re-captured.
      parentOrigin = event.origin
      post({ type: MSG.READY, v: PROTOCOL_VERSION })
      return
    }

    if (parentOrigin === null || event.origin !== parentOrigin) return

    if (type === MSG.REQUEST_TOKEN) {
      const { requestId, action } = data as { requestId?: unknown; action?: unknown }
      if (typeof requestId !== 'string' || typeof action !== 'string') return

      try {
        const token = await deps.getToken(action)
        post({ type: MSG.TOKEN, v: PROTOCOL_VERSION, requestId, token })
      } catch (error) {
        const reason: ErrorReason =
          error instanceof CaptchaError ? error.reason : 'turnstile-error'
        post({ type: MSG.ERROR, v: PROTOCOL_VERSION, requestId, reason })
      }
    }
  }

  return { handleMessage }
}
